/* eslint-disable react/prop-types */
import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  CloudinaryPicture,
  ConceptBadge,
  CruiseLogo,
  Grid,
  GridItem,
} from '@bwoty-web/ui-kit';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import Gallery from '../../common/gallery/Gallery';
import GalleryButton from '../../common/gallery/GalleryButton';
import Video from './Video';
import './topImageView.scss';

const TopImageView = ({
  siteId,
  topImageContent,
  count,
  userDevice,
  galleryMode,
  hideCloseButton,
  isAppWebView = false,
}) => {
  const isMobile = userDevice === 'mobile';
  const isGlobe = useIsGlobetrotter();
  const [showGallery, setShowGallery] = useState(false);
  const [preselectedCategory, setPreselectedCategory] = useState('all');
  // isReady is used to prevent the gallery from opening before the component is fully loaded
  const [isReady, setIsReady] = useState(!galleryMode);

  const messageEventHandler = useCallback(
    (event) => {
      if (event.origin === window.origin) {
        if (typeof event.data === 'string') {
          // example  'cmd.open.gallery:foodAndBeverage'
          const [command, category] = event.data.split(':');
          if (command === 'cmd.open.gallery') {
            // use category to set tab
            if (category) {
              setShowGallery(true);
              setPreselectedCategory(category);
            } else setShowGallery(true);
          }
        }
      }
    },
    [setShowGallery],
  );

  /* eslint-disable consistent-return */
  useEffect(() => {
    if (typeof window !== 'undefined' && window) {
      window.addEventListener(
        'message',
        (event) => messageEventHandler(event),
        false,
      );

      return window.removeEventListener(
        'message',
        (event) => messageEventHandler(event),
        false,
      );
    }
  }, [messageEventHandler]);

  useEffect(() => {
    if (galleryMode) {
      setShowGallery(true);
    }
    setIsReady(true);
  }, [galleryMode]);

  if (!topImageContent) {
    return null;
  }

  const {
    topImage,
    imageVideoList,
    galleryButtonText,
    galleryHeaderText,
    gallerySections,
    productInfo,
    hasLolloAndBernie,
    cruiseCompanyCode,
    topVideo,
  } = topImageContent;

  const handleOpenGallery = () => {
    if (count > 1 || (imageVideoList && imageVideoList.length > 0))
      setShowGallery(true);
  };

  if (!isReady) {
    return null;
  }

  return (
    <>
      {!showGallery && (
        <div
          className={cx('gw-top-image', {
            'gw-top-image--in-web-view': isAppWebView,
          })}
        >
          {!!topVideo && <Video video={topVideo} />}
          {!topVideo && topImage?.length > 1 && (
            <div className="gw-top-image__picture_mix">
              <CloudinaryPicture
                img={topImage[0]}
                className="gw-top-image__picture gw-top-image__picture_mix--left"
                sizes={topImage[0].sizes}
                alt={topImage[0].alt}
                retina
              />
              <CloudinaryPicture
                img={topImage[1]}
                className="gw-top-image__picture gw-top-image__picture_mix--right"
                sizes={topImage[1].sizes}
                alt={topImage[1].alt}
                retina
              />
            </div>
          )}
          {!topVideo && topImage?.length === 1 && (
            <CloudinaryPicture
              img={topImage[0]}
              className="gw-top-image__picture"
              sizes={topImage[0].sizes}
              alt={topImage[0].alt}
              retina
            />
          )}
          <Grid root className="gw-top-image__content-grid">
            {count && count > 1 && !isAppWebView && (
              <button
                className="gw-top-image__button-layer"
                type="button"
                onClick={handleOpenGallery}
              >
                <span className="visually-hidden">{galleryButtonText}</span>
              </button>
            )}
            <GridItem className="gw-top-image__content">
              {productInfo && productInfo.productType !== '' && !isGlobe && (
                <ConceptBadge
                  siteId={parseInt(siteId, 10)}
                  className="gw-top-image__concept-logo"
                  concept={productInfo?.productType?.toLowerCase()}
                />
              )}
              {count && count > 1 && !isAppWebView && (
                <GalleryButton
                  className="gw-top-image__gallery-button"
                  text={galleryButtonText}
                  count={count}
                />
              )}
              {hasLolloAndBernie && !isGlobe && (
                <div className="gw-top-image__lollo-and-bernie">
                  <img
                    src="https://idun.nltg.com/assets/images/L&B.png"
                    alt="lollo&bernie"
                    height={80}
                  />
                </div>
              )}
              {cruiseCompanyCode && (
                <CruiseLogo
                  className="gw-top-image__cruise-logo"
                  cruiseCode={cruiseCompanyCode}
                  responsive
                />
              )}
            </GridItem>
          </Grid>
        </div>
      )}
      {showGallery && (
        <Gallery
          open={showGallery}
          close={() => setShowGallery(false)}
          preselectedCategory={preselectedCategory}
          setPreselectedCategory={setPreselectedCategory}
          heading={galleryHeaderText}
          galleryCategories={gallerySections}
          images={imageVideoList}
          isMobile={isMobile}
          siteId={siteId}
          hideCloseButton={hideCloseButton}
        />
      )}
    </>
  );
};

TopImageView.propTypes = {
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  topImageContent: PropTypes.shape({
    cruiseCompanyCode: PropTypes.string,
    topImage: PropTypes.arrayOf(
      PropTypes.shape({
        cloudinaryUriTemplate: PropTypes.string,
        sizes: PropTypes.arrayOf(PropTypes.shape({})),
        alt: PropTypes.string,
      }),
    ),
    productInfo: PropTypes.shape({
      isConceptHotel: PropTypes.bool,
      productType: PropTypes.string,
    }),
    movies: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    galleryButtonText: PropTypes.string,
    galleryHeaderText: PropTypes.string,
    gallerySections: PropTypes.shape({}),
    imageVideoList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    lolloAndBernieBadge: PropTypes.shape({
      sources: PropTypes.arrayOf(PropTypes.shape({})),
      alt: PropTypes.string,
    }),
    cruiseBadge: PropTypes.shape({
      cloudinaryUriTemplate: PropTypes.string,
      alt: PropTypes.string,
      sizes: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    topVideo: PropTypes.shape({}),
  }).isRequired,
  count: PropTypes.number,
  userDevice: PropTypes.string,
  galleryMode: PropTypes.bool,
  hideCloseButton: PropTypes.bool,
};

TopImageView.defaultProps = {
  count: undefined,
  userDevice: '',
  galleryMode: false,
  hideCloseButton: false,
};

export default TopImageView;
